import * as THREE from 'three';
import { shadowBlastEffect as LOOK, shadowCantripEffect as SHADE } from '../../../content/spells/effects.js';
import type { CombatVisualEvent } from '../../../shared/game/combat-types.js';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import { DarkSmoke } from './dark-smoke.js';
import { mulberry32 } from './blood-model.js';
import type { UnitAnimator } from '../entities/unit-animator.js';

const THREAD = 16, SMEAR = 11, TRAIL = 48;
/** Seconds a puff of a claw's trail hangs in the air, and the least a claw moves (m) before it sheds another. */
const TRAIL_LIFE = .5, SHED_EVERY = .035;
/** Seconds a thread takes to run from its Shadow to the foe, and a smear to be dragged across the target. */
const REACH = .25, DRAG = .12;
interface Thread { shadowId: string; targetId: string; at: number; seed: number }
interface Wake { x: number; y: number; z: number; born: number; speed: number; seed: number }
interface Trail { wakes: Wake[]; last: (THREE.Vector3 | undefined)[]; lastAt: number }
interface Smear { targetId: string; at: number; across: THREE.Vector3; seed: number; random: () => number; offsets: number[] }

/**
 * The Shadowmancer's two quieter effects, in Shadow Blast's black smoke: when the Shadows are commanded a thin cold thread of it runs from
 * each one to the foe and holds there while they go for it, and where a Shadow's claws pass (Shadow Touch, which draws no blood) they leave a
 * smear of it clinging to the target that hisses away upward. Each claw drags a trail of it through the air as the blow is swung, hanging
 * a moment where it passed, thickest where the claw moved fastest. Presentation only: both answer the server's events.
 */
export class ShadowmancerEffects {
  private smoke = new Map<object, DarkSmoke>();
  private threads: Thread[] = [];
  private smears: Smear[] = [];
  private trails = new Map<string, Trail>();
  private pending: { event: CombatVisualEvent; at: number }[] = [];
  get visualObjects(): THREE.Object3D[] { return [...this.smoke.values()].map(smoke => smoke.mesh); }
  constructor(private scene: THREE.Scene, private heights: Map<string, number>, private groundHeight: (x: number, z: number) => number = () => 0) {}
  receive(event: CombatVisualEvent, at: number) {
    if ((event.kind === 'ability' && event.abilityId === 'command-shadows') || (event.kind === 'impact' && event.attackId === 'shadow-touch')) this.pending.push({ event, at });
  }
  update(snapshot: WorldSnapshot, time: number, positions: ReadonlyMap<string, THREE.Object3D>, animators: ReadonlyMap<string, UnitAnimator> = new Map()) {
    let waiting = 0;
    for (const item of this.pending) { if (time + .001 >= item.at) this.start(item.event, item.at, snapshot); else this.pending[waiting++] = item; }
    this.pending.length = waiting;
    this.threads = this.threads.filter(thread => this.drawThread(thread, time, positions));
    this.smears = this.smears.filter(smear => this.drawSmear(smear, time, positions));
    this.drawTrails(time, animators, positions);
  }
  private start(event: CombatVisualEvent, at: number, snapshot: WorldSnapshot) {
    if (event.kind === 'ability') {
      const shadows = (snapshot.summonedEnemies ?? []).filter(unit => unit.summoner === event.actorId && unit.appearance?.rig === 'shadow');
      shadows.forEach((shadow, i) => this.threads.push({ shadowId: shadow.id, targetId: event.targetId, at: at + i * .05, seed: (event.id * 7 + i) * .618 % 1 }));
      return;
    }
    const random = mulberry32(event.id * 6151);
    // Dragged across the target, square to the line the blow came along.
    const along = new THREE.Vector3(event.to.x - event.from.x, 0, event.to.z - event.from.z);
    if (along.lengthSq() < 1e-4) along.set(0, 0, 1);
    const across = new THREE.Vector3(-along.z, 0, along.x).normalize().multiplyScalar(random() < .5 ? -1 : 1);
    this.smears.push({ targetId: event.targetId, at, across, seed: random(), random, offsets: Array.from({ length: SMEAR }, () => random()) });
  }
  private pool(key: object, count: number, name: string) {
    let smoke = this.smoke.get(key);
    if (!smoke) { smoke = new DarkSmoke(count, LOOK.color, LOOK.rim, name); this.scene.add(smoke.mesh); this.smoke.set(key, smoke); }
    return smoke;
  }
  private release(key: object) { this.smoke.get(key)?.dispose(); this.smoke.delete(key); }
  private chest(id: string, positions: ReadonlyMap<string, THREE.Object3D>, share: number) {
    const unit = positions.get(id);
    if (!unit?.visible) return undefined;
    const { x, z } = unit.position;
    return new THREE.Vector3(x, this.groundHeight(x, z) + (this.heights.get(id) ?? 1.6) * share, z);
  }
  /** One thread: it runs out from its Shadow to the foe, sags a little and wavers, then thins away. Returns false once it is gone. */
  private drawThread(thread: Thread, time: number, positions: ReadonlyMap<string, THREE.Object3D>) {
    const age = time - thread.at, from = this.chest(thread.shadowId, positions, .6), to = this.chest(thread.targetId, positions, .55);
    if (age >= SHADE.thread) { this.release(thread); return false; }
    const smoke = this.pool(thread, THREAD, 'command-shadows-thread');
    if (age < 0 || !from || !to) { for (let i = 0; i < THREAD; i++) smoke.hide(i); smoke.commit(time); return true; }
    const run = THREE.MathUtils.smoothstep(age, 0, REACH), fade = 1 - THREE.MathUtils.smoothstep(age, SHADE.thread * .55, SHADE.thread);
    const point = new THREE.Vector3(), side = new THREE.Vector3(to.z - from.z, 0, from.x - to.x).normalize();
    for (let i = 0; i < THREAD; i++) {
      const t = (i + .5) / THREAD;
      if (t > run) { smoke.hide(i); continue; }
      const waver = Math.sin(t * 11 + time * 7 + thread.seed * 6) * .05 * Math.sin(t * Math.PI);
      point.copy(from).lerp(to, t).addScaledVector(side, waver); point.y -= Math.sin(t * Math.PI) * .12;
      smoke.set(i, point.x, point.y, point.z, .1 + .05 * Math.sin(i * 1.7 + time * 3), .8 * fade, .8, time * .2 + t, thread.seed + i * .13);
    }
    smoke.commit(time);
    return true;
  }
  /** One smear: dragged across the target's chest, clinging to it as it moves, then lifting and thinning as it hisses away. */
  private drawSmear(smear: Smear, time: number, positions: ReadonlyMap<string, THREE.Object3D>) {
    const age = time - smear.at, centre = this.chest(smear.targetId, positions, .58), height = this.heights.get(smear.targetId) ?? 1.6;
    if (age >= SHADE.smear + .4 || !centre) { this.release(smear); return false; }
    const smoke = this.pool(smear, SMEAR, 'shadow-touch-smear');
    for (let i = 0; i < SMEAR; i++) {
      const t = i / (SMEAR - 1), born = t * DRAG, life = age - born, q = life / (SHADE.smear * (.75 + .5 * smear.offsets[i]));
      if (life < 0 || q >= 1) { smoke.hide(i); continue; }
      // A diagonal rake, high on one side to low on the other, a hand's depth in front of the body.
      const x = centre.x + smear.across.x * (t - .5) * .55, z = centre.z + smear.across.z * (t - .5) * .55;
      const lift = q * q * .45, drift = (smear.offsets[i] - .5) * .08 * q;
      smoke.set(i, x + drift, centre.y + height * (.12 - .24 * t) + lift, z - drift,
        .16 + .1 * smear.offsets[i] + q * .22, .9 * Math.min(1, life * 25) * (1 - q) ** 1.5, .65 - .3 * q, smear.seed + q * .5, smear.offsets[i]);
    }
    smoke.commit(time);
    return true;
  }
  /** Sheds a puff wherever a swinging claw has moved far enough, and lets the ones already shed hang, drift up and thin. */
  private drawTrails(time: number, animators: ReadonlyMap<string, UnitAnimator>, positions: ReadonlyMap<string, THREE.Object3D>) {
    for (const [id, animator] of animators) {
      const claws = positions.get(id)?.visible ? animator.clawTips?.() : undefined;
      let trail = this.trails.get(id);
      if (!claws) { if (trail) trail.last = []; continue; }
      if (!trail) this.trails.set(id, trail = { wakes: [], last: [], lastAt: time });
      const dt = Math.max(1e-3, time - trail.lastAt);
      claws.tips.forEach((tip, hand) => {
        const last = trail!.last[hand];
        if (last && last.distanceTo(tip) >= SHED_EVERY) {
          // Fill the gap with puffs a step apart, so a fast swing leaves a line rather than dots.
          const steps = Math.min(6, Math.floor(last.distanceTo(tip) / SHED_EVERY)), speed = last.distanceTo(tip) / dt;
          for (let k = 1; k <= steps; k++) { const at = last.clone().lerp(tip, k / steps); trail!.wakes.push({ x: at.x, y: at.y, z: at.z, born: time, speed, seed: (trail!.wakes.length * .618 + hand * .3) % 1 }); }
          trail!.last[hand] = tip.clone();
        } else if (!last) trail!.last[hand] = tip.clone();
      });
      trail.lastAt = time;
      if (trail.wakes.length > TRAIL) trail.wakes.splice(0, trail.wakes.length - TRAIL);
    }
    for (const [id, trail] of this.trails) {
      trail.wakes = trail.wakes.filter(wake => time - wake.born < TRAIL_LIFE && time >= wake.born);
      if (!trail.wakes.length && !trail.last.length) { this.release(trail); this.trails.delete(id); continue; }
      const smoke = this.pool(trail, TRAIL, 'shadow-touch-trail');
      for (let i = 0; i < TRAIL; i++) {
        const wake = trail.wakes[i];
        if (!wake) { smoke.hide(i); continue; }
        // Only a swing shows: a claw drawn back slowly leaves next to nothing.
        const age = time - wake.born, q = age / TRAIL_LIFE, fast = THREE.MathUtils.smoothstep(wake.speed, .8, 3.5);
        smoke.set(i, wake.x, wake.y + age * .25, wake.z, (.07 + .09 * fast) * (1 + q * 1.4), .85 * fast * (1 - q) ** 1.6, .85, wake.seed + q * .3, wake.seed);
      }
      smoke.commit(time);
    }
  }
  clear() { for (const key of [...this.smoke.keys()]) this.release(key); this.threads = []; this.smears = []; this.pending = []; this.trails.clear(); }
  dispose() { this.clear(); }
}
